<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MaterialIcon from '@/components/common/MaterialIcon.vue';
import { PRODUCT_DEMO_MODAL_QUERY } from '@/config/mockup.js';

const props = defineProps({
  /** 外部演示站点完整 URL */
  src: { type: String, required: true },
  /** 地址栏展示文案（可省略协议） */
  displayUrl: { type: String, default: '' },
  /** 点击顶栏按钮时追加到 URL 的 query */
  modalQuery: { type: Object, default: () => PRODUCT_DEMO_MODAL_QUERY },
});

const open = defineModel({ type: Boolean, default: false });
const { t } = useI18n();
const iframeLoaded = ref(false);
/** 实际加载地址（可被按钮追加 query） */
const iframeSrc = ref('');
const iframeKey = ref(0);

/** 与体验演示按钮一致：缩到手机宽度时关闭弹窗 */
const MOBILE_MQ = '(max-width: 767px)';
let mobileMql = null;

function onMobileViewportChange(event) {
  if (event.matches && open.value) open.value = false;
}

function bindMobileClose() {
  if (typeof window === 'undefined' || mobileMql) return;
  mobileMql = window.matchMedia(MOBILE_MQ);
  mobileMql.addEventListener('change', onMobileViewportChange);
  if (mobileMql.matches && open.value) open.value = false;
}

function unbindMobileClose() {
  mobileMql?.removeEventListener('change', onMobileViewportChange);
  mobileMql = null;
}

const barUrl = computed(() => {
  const raw = iframeSrc.value || props.src;
  return props.displayUrl && iframeSrc.value === props.src
    ? props.displayUrl
    : raw.replace(/^https?:\/\//, '');
});

function buildUrl(base, query = {}) {
  const url = new URL(base, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  Object.entries(query).forEach(([key, value]) => {
    if (value == null || value === '') {
      url.searchParams.delete(key);
      return;
    }
    url.searchParams.set(key, String(value));
  });
  return url.toString();
}

function resetIframeSrc() {
  iframeSrc.value = props.src;
  iframeKey.value += 1;
  iframeLoaded.value = false;
}

/** 点击后直接传参，通知外部站打开弹窗 */
function openExternalModal() {
  iframeSrc.value = buildUrl(props.src, props.modalQuery || {});
  iframeKey.value += 1;
  iframeLoaded.value = false;
}

function onKeydown(event) {
  if (event.key === 'Escape') open.value = false;
}

watch(open, (isOpen) => {
  document.body.style.overflow = isOpen ? 'hidden' : '';
  if (isOpen) {
    resetIframeSrc();
    bindMobileClose();
    window.addEventListener('keydown', onKeydown);
    return;
  }
  window.removeEventListener('keydown', onKeydown);
});

watch(() => props.src, () => {
  if (open.value) resetIframeSrc();
});

onBeforeUnmount(() => {
  document.body.style.overflow = '';
  window.removeEventListener('keydown', onKeydown);
  unbindMobileClose();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="demo-modal">
      <div
        v-if="open"
        class="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6"
        role="dialog"
        aria-modal="true"
        :aria-label="t('product.demoTitle')"
      >
        <div
          class="demo-modal-backdrop absolute inset-0 bg-slate-900/50"
          @click="open = false"
        />

        <div
          class="demo-modal-panel relative z-10 flex h-[min(92vh,880px)] w-full max-w-[80vw] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl shadow-slate-900/20"
        >
          <!-- 简易浏览器栏 -->
          <div class="flex shrink-0 items-center gap-2 border-b border-border bg-muted/50 px-3 py-2.5 sm:gap-3 sm:px-4">
            <div class="hidden items-center gap-1.5 sm:flex" aria-hidden="true">
              <span class="size-2.5 rounded-full bg-[#ff5f57]" />
              <span class="size-2.5 rounded-full bg-[#febc2e]" />
              <span class="size-2.5 rounded-full bg-[#28c840]" />
            </div>

            <div
              class="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground sm:text-sm"
            >
              <MaterialIcon name="lock" class="shrink-0 text-base text-emerald-600/80" />
              <span class="truncate">{{ barUrl }}</span>
            </div>

            <button
              type="button"
              class="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground sm:text-sm"
              :aria-label="t('product.demoMenuOpenModal')"
              @click="openExternalModal"
            >
              <MaterialIcon name="web_asset" class="text-base" />
              <span class="hidden sm:inline">{{ t('product.demoMenuOpenModal') }}</span>
            </button>

            <!-- <a
              :href="iframeSrc || src"
              target="_blank"
              rel="noopener noreferrer"
              class="hidden shrink-0 items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground sm:inline-flex"
              :aria-label="t('product.demoOpenNew')"
            >
              <MaterialIcon name="open_in_new" class="text-base" />
            </a> -->

            <button
              type="button"
              class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
              :aria-label="t('product.demoClose')"
              @click="open = false"
            >
              <MaterialIcon name="close" class="text-xl" />
            </button>
          </div>

          <div class="relative min-h-0 flex-1 bg-slate-50">
            <div
              v-if="!iframeLoaded"
              class="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-3 text-muted-foreground"
            >
              <span
                class="size-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary"
                aria-hidden="true"
              />
              <p class="text-sm">{{ t('product.demoLoading') }}</p>
            </div>

            <iframe
              :key="iframeKey"
              :src="iframeSrc"
              class="h-full w-full border-0"
              :title="t('product.demoTitle')"
              loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              allow="clipboard-read; clipboard-write; fullscreen"
              @load="iframeLoaded = true"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.demo-modal-enter-active,
.demo-modal-leave-active {
  transition: opacity 0.28s ease;
}

.demo-modal-enter-active .demo-modal-panel,
.demo-modal-leave-active .demo-modal-panel {
  transition:
    transform 0.32s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.28s ease;
}

.demo-modal-enter-active .demo-modal-backdrop,
.demo-modal-leave-active .demo-modal-backdrop {
  transition: opacity 0.28s ease;
}

.demo-modal-enter-from,
.demo-modal-leave-to {
  opacity: 0;
}

.demo-modal-enter-from .demo-modal-panel,
.demo-modal-leave-to .demo-modal-panel {
  opacity: 0;
  transform: scale(0.96) translateY(16px);
}

.demo-modal-enter-from .demo-modal-backdrop,
.demo-modal-leave-to .demo-modal-backdrop {
  opacity: 0;
}
</style>
